import React, { useEffect, useState } from 'react';
import { Button, Modal } from "react-bootstrap";
import Select, { components } from "react-select";
import axios from "axios";
import LightGallery from 'lightgallery/react';
import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lg-thumbnail.css';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';
import bgimage from '../../../../images/vendor/vendor.png';
import { toast } from 'react-toastify';
const customStyles = {
  groupHeading: (provided) => ({
    ...provided,
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#333',
  }),
};

const Group = (props) => (
  <div style={customStyles}>
    <components.Group {...props} /> 
  </div>
);

const AddRawMaterial = ({ show, onClose, vendorId, data, refresh }) => { 
  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const [vendor, setVendor] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedRawIds, setSelectedRawIds] = useState([]); // use rawmaterial_id

  useEffect(() => {
    if (!show || !vendorId) return;

    const fetchCategories = axios.get(`${baseURL}/raw-material-categories-with-materials`);
    const fetchVendor = axios.get(`${baseURL}/managevendor/${vendorId}`);

    Promise.all([fetchCategories, fetchVendor])
      .then(([catRes, vendorRes]) => {
        const categories = catRes.data.data || [];
        setRawData(categories);

        const options = categories.map(cat => ({
          value: cat.category_id,
          label: cat.category_name,
        }));
        setCategoryOptions(options);

        const vendorData = vendorRes.data.vendor;
        setVendor(vendorData);

        // Map vendor rawmaterials using rawmaterial_id
        const vendorRawIds = (vendorData.vendor_rawmaterials || []).map(rm => rm.rawmaterial_id);
        setSelectedRawIds(vendorRawIds);

        // Select first category that contains vendor raw materials
        const categoryWithVendorMaterials = categories.find(cat =>
          cat.raw_materials.some(rm => vendorRawIds.includes(rm.rawmaterial_id))
        );

        if (categoryWithVendorMaterials) {
          setSelectedCategory({
            value: categoryWithVendorMaterials.category_id,
            label: categoryWithVendorMaterials.category_name
          });
        } else if (options.length > 0) {
          setSelectedCategory(options[0]);
        }
      })
      .catch(err => console.error("Data fetch error", err));
  }, [show, vendorId]);

  const handleMaterialCheck = (rawmaterialId) => {
    setSelectedRawIds(prev =>
      prev.includes(rawmaterialId)
        ? prev.filter(id => id !== rawmaterialId)
        : [...prev, rawmaterialId]
    );
  };

  const handleSaveRawMaterials = async () => {
    if (!selectedRawIds.length || !vendorId) {
      alert("Please select at least one raw material.");
      return;
    }

    const payload = {
      vendor_rawmaterials: selectedRawIds.map(id => ({ rawmaterial_id: id })),
    };

    try {
      const response = await axios.post(`${baseURL}/managevendor/${vendorId}/rawmaterials`, payload);
      if (response.data?.message?.includes('success')) { 
        toast.success("Raw materials added successfully!"); 
        onClose();
        refresh();  // <---- refresh parent list here
      } else { 
        toast.error("Failed to add raw materials."); 
      }
    } catch (error) {
      console.error("Error updating vendor raw materials:", error.response?.data || error); 
      toast.error("Something went wrong.");
    }
  };

  return (
    <Modal className="modal fade" show={show} size="lg">
      <div className="modal-content">
        <div className="modal-header" style={{ backgroundColor: '#3ab384' }}>
          <h3 className="modal-title text-white">Add Raw Material</h3>
          <Button variant="" type="button" className="close px-6 py-2" onClick={onClose}>
            <span className="text-white">×</span>
          </Button>
        </div>
        <div className="modal-body">
          <div className="row">
            {/* Vendor Info */}
            <div className='col-lg-6 col-md-6 col-sm-12 d-flex flex-column mb-3'>
              <div className='d-flex flex-row gap-2 align-items-center justify-content-center'>
                {vendor && (
                  <>
                    <LightGallery speed={500} plugins={[lgThumbnail, lgZoom]}>
                      <div data-src={data.vendor_image || bgimage} className='border rounded px-1 py-0'>
                        <img src={data.vendor_image || bgimage} className='cursor-pointer w-55px h-55px' />
                      </div>
                    </LightGallery>
                    <div className='d-flex flex-column'>
                      <label className='text-gray-800 font-w500 fs-7'>{vendor.vendor_businessname}</label>
                      <label className='text-dark fs-7 font-w500'>{vendor.vendor_mobileno}</label>
                      <label className='text-danger fs-6 font-w500'>{vendor.vendor_category?.vendorcategory_name}</label>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Category Dropdown */}
            <div className='col-lg-6 col-md-6 col-sm-12 d-flex flex-column mb-3'>
              <label className='text-gray-800 font-w500 fs-7'>
                Raw Material Category<span className='required'>*</span>
              </label>
              <Select
                placeholder="Select Raw Material Category"
                value={selectedCategory}
                options={categoryOptions}
                onChange={setSelectedCategory}
                components={{ Group }}
                styles={customStyles}
              />
            </div>
          </div>

          {/* Raw Material Checkboxes */}
          <div className='row scroll-y mh-350px py-2'>
            {rawData
              .filter(cat => cat.category_id === selectedCategory?.value)
              .flatMap(cat => cat.raw_materials)
              .map(material => (
                <div className="col-lg-6 mb-2" key={material.rawmaterial_id}>
                  <div className='form-check-inline mt-1'>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={selectedRawIds.includes(material.sno)}
                      onChange={() => handleMaterialCheck(material.sno)}
                    />
                    <label
                      className={`fs-7 mb-0 font-w500 ms-2 ${
                        selectedRawIds.includes(material.rawmaterial_id) ? 'text-success' : 'text-black'
                      }`}
                    >
                      {material.rawmaterial_name}
                    </label>
                    <label className='text-gray-700 font-w400 fs-8 d-block ms-4 px-2'>
                      {selectedCategory?.label}
                    </label>
                  </div>
                </div>
              ))}
          </div>

          <div className="d-flex align-items-center justify-content-end gap-3 mt-4">
            <Button className="btn btn-sm btn-primary" onClick={handleSaveRawMaterials}>
              Add Raw Material <i className="fas fa-angle-double-right ms-2"></i>
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AddRawMaterial;
